/**
 * The hydrographic-data provider seam.
 *
 * ChartSource already hides HOW a cell is fetched (bundled file, downloaded
 * pack, remote pack). This layer answers the questions above that one: WHO
 * supplies the data, under what licence, with what attribution, and behind what
 * gate. It exists so that the day a UKHO or IC-ENC agreement lands, licensed
 * charts become a new provider registered here, not a rewrite of the renderer,
 * residency, or the pack machinery. That is the whole design intent: the
 * licensing conversation (docs/licensing-enquiries.md) runs on calendar time,
 * so the code has to be ready before the answer arrives.
 *
 * Two design decisions worth recording:
 *
 * The registry is ordered and static-first. Providers register at module load,
 * the map reads whatever is registered at plan time, and pack sources flow
 * through their provider rather than being globbed in beside it. Registration
 * order is draw-candidate order only in the sense that earlier providers list
 * first; residency ranks cells on geometry and band, never on provider, so
 * order carries no safety meaning.
 *
 * `entitled` is a promise-returning gate rather than a boolean, because for
 * licensed data the honest answer involves asking a server about a
 * subscription. The free providers answer true synchronously. Residency treats
 * an unentitled provider exactly like a provider with no cells, which is the
 * failure mode we want: charts quietly absent, never half-drawn.
 */
import type { ChartSource } from '@/types';
import { noaaChartSource, trainingChartSource } from './training';
import { useChartSourcesStore } from '@/store/chartSources';

export type ProviderLicence = 'public-domain' | 'fictional' | 'licensed';

export interface HydrographicDataProvider {
  /** Stable id, e.g. 'noaa', 'rya-training', 'ukho'. */
  id: string;
  /** Shown to the user in the chart list and the provenance readout. */
  name: string;
  /**
   * The attribution line the data's terms require us to carry. NOAA asks for
   * acknowledgement and a currency disclaimer; licensed providers will dictate
   * theirs in the agreement.
   */
  attribution: string;
  licence: ProviderLicence;
  /**
   * The sources this provider can offer RIGHT NOW: bundled cells plus whatever
   * packs are downloaded or switched on. Read fresh each call, because packs
   * come and go at runtime.
   */
  sources(): ChartSource[];
  /**
   * Whether this provider's data may be used at all. Free data answers true.
   * Licensed data asks the entitlement service, and a lapsed subscription
   * turning this false is exactly how the hard-expiry design in
   * docs/entitlement-design.md reaches the chart: the provider's sources stop
   * being offered, so its cells leave residency like any other removed source.
   */
  entitled(): Promise<boolean>;
}

/** The fictional RYA training charts: our own data, always available. */
export const ryaTrainingProvider: HydrographicDataProvider = {
  id: 'rya-training',
  name: 'RYA training charts',
  attribution: 'Fictional training charts © Ardent Training. Not for navigation.',
  licence: 'fictional',
  sources: () => [trainingChartSource],
  entitled: () => Promise.resolve(true),
};

/**
 * NOAA ENC: US public domain. The bundled California cells plus every vector
 * pack, since today all packs are built from the NOAA set. When a second pack
 * producer appears, packs will need a producer tag so each provider claims its
 * own; until then inventing one would be speculation.
 */
export const noaaProvider: HydrographicDataProvider = {
  id: 'noaa',
  name: 'NOAA ENC',
  attribution:
    'Chart data from NOAA ENCs (US public domain). Not an official chart; refer to official publications for navigation.',
  licence: 'public-domain',
  sources: () => [noaaChartSource, ...useChartSourcesStore.getState().packCellSources],
  entitled: () => Promise.resolve(true),
};

const registry: HydrographicDataProvider[] = [ryaTrainingProvider, noaaProvider];

/**
 * Register a provider. This is the line a UKHO/IC-ENC integration calls, and
 * the intent is that it is the ONLY line it has to call to reach the chart:
 * everything downstream (residency, quilting, packs, provenance) works off
 * sources and CellMeta, which the new provider supplies like any other.
 */
export function registerHydroProvider(p: HydrographicDataProvider): void {
  if (registry.some((r) => r.id === p.id)) return;
  registry.push(p);
}

export function hydroProviders(): HydrographicDataProvider[] {
  return [...registry];
}

/**
 * Every chart source the registered, entitled providers offer right now. This
 * is what the map's residency asks for; it deliberately does not know which
 * provider a source came from.
 *
 * Entitlement is read from the last resolved answer per provider, refreshed in
 * the background on each call. Sync-with-cached-answer rather than async,
 * because residency replans on every pan and cannot await a server round trip;
 * the price is that a revocation takes one plan cycle to bite, which against a
 * 30-day expiry ladder is nothing.
 */
const entitledCache = new Map<string, boolean>();

export function hydroSources(): ChartSource[] {
  const out: ChartSource[] = [];
  for (const p of registry) {
    const known = entitledCache.get(p.id);
    if (known === undefined) {
      // First sight of this provider: assume its own default until the promise
      // answers. Free providers resolve immediately; a licensed provider should
      // register only after its first entitlement check anyway.
      entitledCache.set(p.id, true);
    }
    void p
      .entitled()
      .then((ok) => entitledCache.set(p.id, ok))
      .catch(() => entitledCache.set(p.id, false));
    if (entitledCache.get(p.id)) out.push(...p.sources());
  }
  return out;
}

/** The attribution lines the current providers require, for the about screen. */
export function hydroAttributions(): { provider: string; text: string }[] {
  return registry.map((p) => ({ provider: p.name, text: p.attribution }));
}
